/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors, type ValidatorFn } from '@angular/forms';
import { type BaseRequestOptionsArgs, type Capacity, type HttpResponse, type Rest, type SelectOption, Consts, FormControl, HttpService, PermissionService, UtilService } from '@dm/core';
import { StoragePoolAndLunWorkModeE } from 'app/common/constants';
import { MO } from 'app/common/constants/mo';
import { ToolService } from 'app/common/services';
import { SimpleModeService } from 'app/common/services/simple-mode.service';
import { TaskService } from 'app/common/services/task.service';
import { type DataAffinityModeComp, type HyperCdpSchedule, type Lun, type LunParam, type Mapping, type ProtectionLun, type SmartPartition, type StoragePool } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { type Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { type NewLunInfo } from '../../lun-group/create/lun/lun.component';
import { type CreateLunParams, LunsConst, RelationType, USAGE_TYPE } from '../../lun-group/luns.const';
import { type CreateLunsInfo } from '../../lun-group/model';

@Injectable()
export class LunService {
    constructor(
        private str: LunsConst,
        private httpService: HttpService,
        private utilService: UtilService,
        private taskService: TaskService,
        private tool: ToolService,
        private permission: PermissionService,
        private simpleModeService: SimpleModeService) {
    }
    getRate = this.tool.getRate;
    // 查询远端LUN信息
    getRemoveLUN(remoteLunId: string, param: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('remoteLun.get').format({ id: remoteLunId });
        rest.setParams(param);
        return this.httpService.get(rest, options);
    }

    // 获取所有存储池
    getAllPools(options?: BaseRequestOptionsArgs, vstoreId?: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.getBatch');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.httpService.get(rest, options);
    }

    // 获取所有硬盘
    getAllDisks(options?: BaseRequestOptionsArgs): Observable<any[]> {
        const rest = this.httpService.makeUrl('disk.getBatch');
        return this.tool.getAllData(rest, options);
    }

    // 获取所有主机组
    getHostGroup(options?: BaseRequestOptionsArgs): Observable<any[]> {
        const rest = this.httpService.makeUrl('hostGroup.getBatch');
        return this.tool.getAllData(rest, options);
    }

    // 获取所有主机
    getHost(options?: BaseRequestOptionsArgs): Observable<any[]> {
        const rest = this.httpService.makeUrl('host.getBatch');
        return this.tool.getAllData(rest, options);
    }

    // 获取单个主机
    getHostById(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('host.get').format(id);
        return this.httpService.get(rest, options);
    }

    // 获取所有主机的Rest
    getHostRest(vstoreId: string): Rest {
        return this.httpService.makeUrl('host.getBatch').setParams({ vstoreId });
    }

    // 获取所有端口组
    getPortGroup(options?: BaseRequestOptionsArgs): Observable<any[]> {
        const rest = this.httpService.makeUrl('portGroup.getBatch');
        return this.tool.getAllData(rest, options);
    }

    // 获取单个端口组
    getPortGroupById(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('portGroup.get').format(id);
        return this.httpService.get(rest, options);
    }

    // 查询LUN映射
    getMappedObjects(hostId: number, relationType = RelationType.LUN, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('mapping.mappedObjects')
            .setParams({
                ASSOCIATEOBJTYPE: MO.HOST_TYPE,
                ASSOCIATEOBJID: hostId,
                relationType,
                range: '[0-1]',
            });
        return this.httpService.get(rest, options);
    }

    /**
     * 关联查询LUN映射Rest
     */
    getAssociateMappedObjectsRest(lunId: string): Rest {
        const rest = this.httpService.makeUrl('host.getAssociate').setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: lunId,
        });

        return rest;
    }

    /**
     * 关联查询LUN映射主机数量Rest
     */
    getAssociateMappedObjectsCountRest(lunId: string): Rest {
        const rest = this.httpService.makeUrl('host.count').setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: lunId,
        });

        return rest;
    }

    /**
     * 查询LUN映射数量Rest
     */
    getMappedObjectsCountRest(lunId: string): Rest {
        const rest = this.httpService.makeUrl('mapping.mappedObjectsCount').setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: lunId,
        });

        return rest;
    }

    // 查询LUN组映射
    getMappedLunGroupObjectsRest(hostId: number, relationType = RelationType.LUNGROUP): Rest {
        const rest = this.httpService.makeUrl('mapping.mappedObjects')
            .setParams({
                ASSOCIATEOBJTYPE: MO.HOST_TYPE,
                ASSOCIATEOBJID: hostId,
                relationType,
                range: '[0-1]',
            });
        return rest;
    }

    // 获取所有端口组Rest
    getPortGroupRest(vstoreId: string): Rest {
        return this.httpService.makeUrl('portGroup.getBatch').setParams({ vstoreId });
    }

    // 映射移除端口组
    removePortGroup(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('mapping.removePortGroup');
        return this.httpService.put(rest, params, options);
    }

    // 获取LUN关联的LUN组
    getAssociateLunGroup(lunId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lunGroup.associate');
        rest.setParams({
            TYPE: MO.LUN_GROUP,
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: lunId,
        });
        return this.httpService.get(rest, options);
    }

    // 查询单个LUN
    getLUN(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.get').format(id);
        return this.httpService.get(rest, options);
    }

    // 获取所有LUN
    getLunAll(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.getBatch')
            .setFilters([
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
            ]);
        return this.httpService.get(rest, options);
    }

    // 获取所有直通LUN
    getDirectLunAll(parentId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.getBatch')
            .setFilters([
                { field: 'SUBTYPE', value: '0', matchMode: '::' },
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
                // 根据所属硬盘过滤
                { field: 'PARENTID', value: parentId, matchMode: '::' },
            ]);
        return this.httpService.get(rest, options);
    }

    // 单个查询存储池
    getStoragePool(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.get').format(id);
        return this.httpService.get(rest, options);
    }

    // 获取主表格普通LUN和总个数
    getMainLunRest(vstoreId?: string): Rest {
        return this.httpService.makeUrl('lun.getBatch', undefined, true).setParams({ vstoreId })
            .setFilters([
                // 0: 普通LUN； 6: 计算节点un
                { field: 'SUBTYPE', matchMode: '::', value: '0_6' },
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
            ]);
    }

    getMemberLunRest(groupId: string): Rest {
        return this.httpService.makeUrl('lun.associate')
            .setParams({
                TYPE: MO.LUN,
                ASSOCIATEOBJTYPE: MO.LUN_GROUP,
                ASSOCIATEOBJID: groupId,
            });
    }

    getMemberLunCountRest(groupId: string): Rest {
        return this.httpService.makeUrl('lun.count')
            .setParams({
                TYPE: MO.LUN,
                ASSOCIATEOBJTYPE: MO.LUN_GROUP,
                ASSOCIATEOBJID: groupId,
            });
    }

    /**
     * 获取同一个LUN组其他映射的LUN
     */
    getMemberLunCount(groupId: string): Observable<HttpResponse> {
        return this.httpService.get(this.getMemberLunCountRest(groupId));
    }

    getAssociateLunCountRest(groupId: string): Rest {
        const rest = this.httpService.makeUrl('lun.count')
            .setParams({
                TYPE: MO.LUN,
                ASSOCIATEOBJTYPE: MO.LUN_GROUP,
                ASSOCIATEOBJID: groupId,
            });
        return rest;
    }

    // 关联查询LUN组中的LUN
    getAssociateLun(groupId: string | number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.associate');
        rest.setParams({
            TYPE: MO.LUN,
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: groupId,
        });
        return this.httpService.get(rest, options);
    }

    // 关联查询LUN组中的LUN个数
    getAssociateLunCount(groupId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getAssociateLunCountRest(groupId);
        return this.httpService.get(rest, options);
    }

    // 获取可以加入LUN组的LUN
    getUnAssociateLunRest(groupId?: string, vstoreId?: string): Rest {
        const rest = this.httpService.makeUrl('lun.unassociate')
            .setFilters([
                // 0: 普通LUN； 6: 计算节点un
                { field: 'SUBTYPE', matchMode: '::', value: '0_6' },
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
            ])
            .setParams({
                vstoreId,
                TYPE: MO.LUN,
                ASSOCIATEOBJTYPE: MO.LUN_GROUP,
                ASSOCIATEOBJID: groupId,
            });
        return rest;
    }

    // 获取所有可加入到LUN组中的LUN
    getAllLunRest(isAdd2LunGroup?: boolean, vstoreId?: string): Rest {
        return this.httpService.makeUrl('lun.getBatch').setParams({ vstoreId })
            .setFilters([
                // 0: 普通LUN； 6: 计算节点un
                { field: 'SUBTYPE', matchMode: '::', value: '0_6' },
                { field: 'ISADD2LUNGROUP', matchMode: '::', value: isAdd2LunGroup ? 'false' : null },
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
            ]);
    }

    /**
     * 获取所有应用类型
     */
    getWorkloadTypes(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        // templateType 0:LUN;1:文件系统
        let rest = this.httpService.makeUrl('workloadType.getBatch')
            .setParams({ isDetailInfo: true, templateType: '0' });

        // 入门级型号与低端型号支持极简需求拼接请求 【方法里面会设置filterGroup，如果后续加filter需要考虑影响性】
        if (ProductConfig.IsSupportSimpleMode) {
            rest = this.simpleModeService.getWorkLoadTypeRest(rest);
        }

        return this.httpService.get(rest, options);
    }

    /**
     * 获取应用类型
     * @param id
     */
    getWorkloadType(id: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('workloadType.get').format(id)
            .setParams({ isDetailInfo: true });
        return this.httpService.get(rest);
    }

    // 查询存储池下一个可用ID
    getNextId(type: number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.nextavailableid');
        rest.setParams({ TYPE: type });
        return this.httpService.get(rest, options);
    }

    // 获取智能GUI的LUN数据
    getSmartguiLun(vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartgui.getLun', 'V2').setParams({ vstoreId });
        return this.httpService.get(rest, options);
    }

    // 获取智能GUI的LUN数据（从LUN组里创建LUN时使用）
    getLunFromGroup(lunGroupName: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartgui.getLunFromGroup', 'V2').format(lunGroupName).setParams({ vstoreId });
        return this.httpService.get(rest, options);
    }

    // 创建LUN
    create(params: LunParam, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.post');
        return this.httpService.post(rest, params, options);
    }

    // 批量创建LUN
    createBatch(params: CreateLunsInfo[], options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.createBatch');
        return this.httpService.post(rest, params, options);
    }

    createByOmtask(objName: string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.post', 'V2');
        return this.taskService.post('createlun', objName, rest, params, options);
    }

    // 修改LUN
    modifyLun(params: LunParam, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.put');
        return this.httpService.put(rest, params, options);
    }

    // 扩容LUN
    expand(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.expand');
        return this.httpService.put(rest, params, options);
    }

    // 删除LUN
    delete(id: string, isDelayDelete: string, runningStatus: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        let rest = this.httpService.makeUrl('lun.delete');

        // 微存储直通模式屏蔽回收站：删除LUN时不下发该字段【是否移除到回收站】
        if (!SWITCH.IS_DIRECT_MODE) {
            rest.format(id);
            rest.setParams({ isDelayDelete });
        } else {
            rest = this.httpService.makeUrl('lun.deleteDirectLun');
            // 微存储直通模式以硬盘粒度删除Namespace
            rest.setParams({ PARENTID: id });
            // 微存储直通模式以硬盘粒度删除Namespace
            rest.setParams({ PARENTTYPE: 10 });
            rest.setParams({ namespaceStatus: runningStatus });
        }

        return this.httpService.delete(rest, options);
    }

    // 获取最大主机Lun id
    getMaxNumOfHostLunIds(opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('mapping.maxNumOfHostLunIds');
        return this.httpService.get(rest, opt);
    }


    // 长度范围错误
    lengthErr(min: number, max: number): string {
        return this.str.get('common.length_msg', [min, max]);
    }

    // 数字范围提示
    rangeTip(min: number, max: number): string {
        return this.str.get('common.valueRange_label', [min, max]);
    }

    // 空错误提示
    requiredErr(name: string): string {
        return this.str.get('common.null_validation', [name]);
    }

    /**
     * 检验容量是否小于最小容量(512KB)
     */
    minCap(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control) {
                return null;
            }
            const currentCap = this.utilService.adaptUnit(control.value.value, control.value.unit, 'Sector').value;
            return currentCap < 1024 ? { minCap: true } : null;
        };
    }

    /**
     * 检验容量是否大于最大容量(256TB)
     */
    maxCap(oriInitCap?: number): ValidatorFn {
        let initCap = oriInitCap;
        return (control: AbstractControl): ValidationErrors | null => {
            if (!initCap) {
                initCap = 0;
            }
            const currentCap = this.utilService.adaptUnit(control.value.value, control.value.unit, 'Sector').value;
            return currentCap + initCap > 256 * 1024 * 1024 * 1024 * 2 ? { maxCap: true } : null;
        };
    }

    requiredCap(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value.value;
            return value === '' || Number(value) === 0 ? { required: true } : null;
        };
    }

    /**
     * 校验存储池不能为空
     */
    poolRequired(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valid = control.value !== '';
            return valid ? null : { poolRequired: true };
        };
    }

    /**
     * 校验硬盘不能为空
     */
    diskRequired(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valid = control.value !== '';
            return valid ? null : { diskRequired: true };
        };
    }

    /**
     * 校验容量格式是否正确
     */
    validateCap(value: string, decimalPoint: number): boolean {
        if (!value) {
            return true;
        }
        let pattern = '\^((\[1-9\]\{1\}\\d*)|(0\{1\}))';
        if (decimalPoint) {
            pattern += '(\.\\d\{0,' + decimalPoint + '\})\?\$';
        } else {
            pattern += '\?\$';
        }

        const regex = new RegExp(pattern, 'g');
        const totalLen = value.length;

        return totalLen <= 12 && regex.test(value);
    }

    bool2String(value: string | boolean): string {
        return value === 'true' || value === true || value === '1' ? this.str.common.enabled0Lab : this.str.common.disabledLab;
    }

    /**
     * 计算容量利用率(与HOST/HOSTGROUP保持一致)
     * @param data 包含容量相关字段的数据
     */
    usageValue(data: Lun): number {
        const capacity = Number(data.CAPACITY) || 0;
        const allocatedCapacity = Number(data.ALLOCCAPACITY) || 0;

        return capacity && allocatedCapacity / capacity * 100;
    }

    // 选中中是否包含eDevLUN
    hasEDevLun(selections: any[]): boolean {
        return selections.some((lun) => Number(lun.USAGETYPE) === USAGE_TYPE.eDev);
    }

    /**
     * 过滤出已经添加到cdp计划中的lun
     */
    joinedCdpSchedule(luns: Lun[]): Lun[] {
        return luns.filter((lun) => lun.hyperCdpScheduleId !== undefined);
    }

    /**
     * 容量精度。MB、sector不支持小数
     * @param capacityUnit 容量单位
     */
    getDecimalPoint(capacityUnit: string): number {
        return capacityUnit === Consts.MB || capacityUnit === Consts.SECTOR ? 0 : 3;
    }

    checkExpandPermission(protectionLun: ProtectionLun): Observable<string> {
        const depPermission = [];
        let hasRemoteProtection = false;

        if (Number(protectionLun.hyperMetroCount) > 0) {
            depPermission.push('hyper_metro_domain_R', 'hyper_metro_pair_W', 'hyper_metro_consistency_group_W');
            hasRemoteProtection = true;
        }

        if (Number(protectionLun.replicationCount) > 0) {
            depPermission.push('remote_replication_W', 'consistency_group_W');
            hasRemoteProtection = true;
        }

        if (Number(protectionLun.drStarCount) > 0) {
            depPermission.push('dr_star_R');
        }

        if (hasRemoteProtection) {
            depPermission.push('remote_device_R', 'task_W');
        }

        return this.permission.checkDepPermission(depPermission);
    }

    /**
     * 是否显示容量偏差提示，不足一个sector
     */
    getLossTip(cap: Capacity): string {
        let capLossTip;
        const { value, unit } = cap;
        if (value && (unit !== Consts.SECTOR && unit !== Consts.MB)) {
            const capByteObj = this.utilService.adaptUnit(value, unit, Consts.BYTE);
            if (Math.floor(capByteObj.value) !== capByteObj.value) {
                capLossTip = this.str.get('storage.lun_task_create_capacity_supportValue_label',
                    [value + ' ' + unit, capByteObj.value / 512]);
            }
        }
        return capLossTip;
    }

    /**
     * 查询关联的映射
     * @param type
     * @param id
     */
    getAssociateMappingsAndCount(type: number, id: string): Observable<{ data: Mapping[]; count: number }> {
        const params = {
            ASSOCIATEOBJTYPE: type,
            ASSOCIATEOBJID: id,
        };
        const countRest = this.httpService.makeUrl('mapping.mappedObjectsCount').setParams(params);
        const dataRest = this.httpService.makeUrl('mapping.mappedObjectsCount').setParams({
            ...params,
            range: '[0-10]',
        });
        return forkJoin([
            this.httpService.get(countRest),
            this.httpService.get(dataRest),
        ])
            .pipe(
                map(([countRes, dataRes]) => ({
                    count: countRes.data.COUNT,
                    data: dataRes.data,
                })),
            );
    }

    getLunName(oriPrefix: string, suffix: number, digits?: number): string {
        let prefix = oriPrefix;
        const MAX_LENGTH = 255;
        if (prefix.length + digits > MAX_LENGTH) {
            prefix = prefix.substring(0, MAX_LENGTH - digits);
        }
        return prefix + suffix.toString().padStart(digits, '0');
    }

    updateNewLuns(prefixCtrl: FormControl, countCtrl: FormControl, startNoCtrl: FormControl, digitsCtrl: FormControl): Lun[] {
        if (prefixCtrl.invalid) {
            return [];
        }

        const prefix = prefixCtrl.value;
        const count = countCtrl.value;
        const startNo = startNoCtrl.value;
        const digits = digitsCtrl.value;

        return this.getNewLunsName(prefix, count, startNo, digits);
    }

    getNewLunsName(prefix, count, startNo, digits?): Lun[] {
        const newLuns = [];
        if (Number(count) === 1) {
            newLuns.push({ NAME: prefix });
        } else {
            for (let i = 0; i < count; i++) {
                newLuns.push({
                    NAME: this.getLunName(prefix, Number(startNo) + i, digits),
                });
            }
        }
        return newLuns;
    }

    getNewLunsNameByNewLunInfo(newLunInfos: NewLunInfo[]): any[] {
        return (newLunInfos || []).reduce(
            (memo, { prefix, count, suffix, digits }) => memo.concat(this.getNewLunsName(prefix, count, suffix, digits)),
            [],
        );
    }

    getCDPSchedule(id: string): Observable<HyperCdpSchedule> {
        const rest = this.httpService.makeUrl('hyperCdpSchedule.get').format(id);
        return this.httpService.get(rest)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 查询缓存池详情
     */
    getSmartCachePool(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.get');
        return this.httpService.get(rest, options);
    }

    /**
     * 关联查询智能缓存分区的信息
     * @param options
     */
    getAssociatePartition(params, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.getAssociatePartition');
        rest.setParams(params);
        return this.httpService.get(rest, options);
    }

    /**
     * 查询缓存池中LUN
     */
    getSmartCachePartitionLun(partitionId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.getAsscociateLun');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.SMARTCACHE_PARTITION,
            ASSOCIATEOBJID: partitionId,
        });
        return this.httpService.get(rest, options);
    }

    /**
     * 查询智能缓存分区中的名称
     * @param partitionId
     */
    getSmartCachePartitionName(partitionId: string): Observable<string> {
        const rest = this.httpService.makeUrl('smartCachePool.getPartitionSingle').format(partitionId);
        return this.httpService.get(rest)
            .pipe(
                map(({ data }: { data: SmartPartition }) => data.name),
            );
    }

    /**
     * 添加关联（Members）
     * id: 分区id
     * associateObjType: mo.lun
     */
    addAsscociate(partitionId: string, lunId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.addAsscociate');
        const body = {
            id: partitionId,
            associateObjType: MO.LUN,
            associateObjID: lunId,
        };
        return this.httpService.put(rest, body, options);
    }

    /**
     * 删除关联（Members）
     */
    removeAsscociate(body: { id: string; associateObjType: number; associateObjID: string }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.removeAsscociate');
        return this.httpService.put(rest, body, options);
    }

    get(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.associate').setParams({ ASSOCIATEOBJTYPE: 57880, ASSOCIATEOBJID: id });
        return this.httpService.get(rest, options);
    }

    /**
     * 更新结束编号
     * @param countCtrl 数量配置
     * @param startNoCtrl 起始编号配置
     */
    updataEndSuffix(countCtrl: FormControl, startNoCtrl: FormControl): string {
        const count = countCtrl.value;
        const startNo = startNoCtrl.value;
        const endSuffixNum = Number(startNo) + Number(count) - 1;
        const endSuffix = endSuffixNum.toString().padStart(startNo.length, '0');
        return endSuffix;
    }

    /**
     * Gets vstore rest
     * @returns
     */
    getVstoreRest(): Rest {
        return this.httpService.makeUrl('vstore.getBatch');
    }

    /**
     * 查询对应ID的租户
     */
    getVstoreById(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('vstore.get').format(id);
        return this.httpService.get(rest, options);
    }

    /**
     * 查询所有控制器
     */
    getAllController(options?: BaseRequestOptionsArgs): Observable<Controller[]> {
        const rest = this.httpService.makeUrl('controller.getBatch');

        return this.httpService.get(rest, options).pipe(map(({ data }: { data: Controller[] }) => data ?? []));
    }

    /**
     * 获取“归属控制器”选项
     */
    getControllerOpts(controllers: Controller[]): SelectOption[] {
        const controllerSelection = [this.str.ownedByAllController];
        controllers.forEach((controller) => {
            controllerSelection.push({
                label: controller.ID,
                value: controller.ID,
            });
        });

        return controllerSelection;
    }

    /**
     * “数据亲和模式”变动回调
     * @param comp 有“数据亲和模式”的组件
     */
    onDataAffinityModeChange(comp: DataAffinityModeComp): void {
        this.handlePoolList(comp);
    }

    /**
     * 处理存储池下拉选项
     * @param comp 有“数据亲和模式”的组件
     */
    handlePoolList(comp: DataAffinityModeComp): void {
        if (!this.isSupportAPLun()) {
            comp.poolList = this.getStoragePoolSelectOpts(comp.oriPools);

            return;
        }

        // 过滤“数据亲和模式”相同的存储池
        const sameWorkModePool = comp.oriPools.filter((pool: StoragePool) => pool.dataAffinityMode === comp.vm?.value.dataAffinityMode);
        comp.poolList = this.getStoragePoolSelectOpts(sameWorkModePool);

        // 如果过滤后的存储池不包含之前选的，则需要清空已选存储池
        const poolCtrl = comp.vm?.get('storagePoolID');
        this.checkSelectionExist(comp.poolList, poolCtrl);
    }

    /**
     * 获取存储池的下拉选项
     * @param oriPools 存储池源数据
     */
    getStoragePoolSelectOpts(oriPools: StoragePool[]): SelectOption[] {
        return oriPools.map((pool: StoragePool) => ({ label: pool.NAME, value: pool.ID }));
    }

    /**
     * 检查已选择的对象是否还在列表里
     * @param targetList 对象列表
     * @param targetCtrl 对象control
     */
    checkSelectionExist(targetList: SelectOption[], targetCtrl: AbstractControl): void {
        if (targetList.some((targetOption: SelectOption) => targetOption.value === targetCtrl?.value)) {
            return;
        }

        // 如果只有一项，则默认选中
        if (targetList.length === 1) {
            targetCtrl.patchValue(targetList[0].value);
            return;
        }

        targetCtrl.patchValue('');
    }

    /**
     * 获取工作模式相关的vm
     */
    getWorkModeVm(): { [key: string]: AbstractControl } {
        if (this.isSupportAPLun()) {
            return {
                dataAffinityMode: new FormControl(StoragePoolAndLunWorkModeE.AA_MODE),
                OWNINGCONTROLLER: new FormControl(this.str.ownedByAllController.value),
            };
        }

        return {};
    }

    /**
     * 处理工作模式相关参数
     * @param params 创建LUN参数
     * @param oriParams vm表单的值
     */
    setWorkModeParams(params: LunParam | CreateLunParams | CreateLunsInfo, originParams): void {
        // 避免params直接取的表单的值，先将“工作模式”删了，在下面重新赋值
        delete params.dataAffinityMode;
        delete params.OWNINGCONTROLLER;

        // “工作模式”是AP的LUN，才下发参数，兼容低版本（低版本的工作模式默认是AA）
        if (originParams.dataAffinityMode === StoragePoolAndLunWorkModeE.AP_MODE) {
            params.dataAffinityMode = originParams.dataAffinityMode;
            params.OWNINGCONTROLLER = originParams.OWNINGCONTROLLER;
        }
    }

    /**
     * 是否支持AP LUN（微存储直通模式不支持）
     */
    isSupportAPLun = (): boolean => FeatureConfig.IsSupportDataAffinityMode && !SWITCH.IS_DIRECT_MODE;
}

export const DEFAULT_CAPACITY_UNIT = Consts.GB;
